import { useState, useEffect, useCallback } from 'react';
import { TokenInfo } from '../types';
import { JupiterApiService, JupiterRouter } from '../services/jupiterApi';

export const useJupiterApi = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const searchTokens = useCallback(async (query: string): Promise<TokenInfo[]> => {
    if (!query.trim()) return [];
    
    setLoading(true);
    setError(null);
    try {
      const results = await JupiterApiService.searchTokens(query);
      return results;
    } catch (err) {
      setError('Erreur lors de la recherche de tokens');
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  const getTokenPrice = useCallback(async (address: string): Promise<TokenInfo | null> => {
    setLoading(true);
    setError(null);
    try {
      const result = await JupiterApiService.getTokenPrice(address);
      return result;
    } catch (err) {
      setError('Erreur lors de la récupération du prix');
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const getTrendingTokens = useCallback(async (): Promise<TokenInfo[]> => {
    setLoading(true);
    setError(null);
    try {
      const results = await JupiterApiService.getTrendingTokens();
      return results;
    } catch (err) {
      setError('Erreur lors de la récupération des tokens populaires');
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  const getRecentTokens = useCallback(async (limit: number = 30): Promise<TokenInfo[]> => {
    setLoading(true);
    setError(null);
    try {
      const results = await JupiterApiService.getRecentTokens(limit);
      return results;
    } catch (err) {
      setError('Erreur lors de la récupération des tokens récents');
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  const getVerifiedTokens = useCallback(async (): Promise<TokenInfo[]> => {
    setLoading(true);
    setError(null);
    try {
      const results = await JupiterApiService.getVerifiedTokens();
      return results;
    } catch (err) {
      setError('Erreur lors de la récupération des tokens vérifiés');
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  const getTopTradedTokens = useCallback(async (
    interval: '5m' | '1h' | '6h' | '24h' = '24h', 
    limit: number = 20
  ): Promise<TokenInfo[]> => {
    setLoading(true);
    setError(null);
    try {
      const results = await JupiterApiService.getTopTradedTokens(interval, limit);
      return results;
    } catch (err) {
      setError('Erreur lors de la récupération des tokens les plus tradés');
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  const getAvailableRouters = useCallback(async (): Promise<JupiterRouter[]> => {
    setLoading(true);
    setError(null);
    try {
      const results = await JupiterApiService.getAvailableRouters();
      return results;
    } catch (err) {
      setError('Erreur lors de la récupération des routeurs');
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  const cleanCache = useCallback(() => {
    JupiterApiService.cleanExpiredCache();
  }, []);

  return {
    searchTokens,
    getTokenPrice,
    getTrendingTokens,
    getRecentTokens,
    getVerifiedTokens,
    getTopTradedTokens,
    getAvailableRouters,
    cleanCache,
    loading,
    error
  };
};

// Hook pour suivre les prix en temps réel
export const useTokenPrices = (tokenAddresses: string[], refreshInterval = 30000) => {
  const [prices, setPrices] = useState<Record<string, TokenInfo>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdate, setLastUpdate] = useState<Date | null>(null);

  const fetchPrices = useCallback(async () => {
    if (tokenAddresses.length === 0) return;
    
    setLoading(true);
    setError(null);
    try {
      const newPrices = await JupiterApiService.getTokenPrices(tokenAddresses);
      setPrices(newPrices);
      setLastUpdate(new Date());
    } catch (err) {
      setError('Erreur lors de la mise à jour des prix');
      console.error('Erreur useTokenPrices:', err);
    } finally {
      setLoading(false);
    }
  }, [tokenAddresses]);

  useEffect(() => {
    fetchPrices();
    
    // Configurer l'intervalle de rafraîchissement
    const interval = setInterval(fetchPrices, refreshInterval);
    
    // Nettoyer le cache toutes les 5 minutes
    const cacheCleanInterval = setInterval(() => {
      JupiterApiService.cleanExpiredCache();
    }, 5 * 60 * 1000);
    
    return () => {
      clearInterval(interval);
      clearInterval(cacheCleanInterval);
    };
  }, [fetchPrices, refreshInterval]);

  return { 
    prices, 
    loading, 
    error, 
    lastUpdate,
    refetch: fetchPrices
  };
};

// Hook pour suivre un token spécifique
export const useTokenPrice = (tokenAddress: string, refreshInterval = 30000) => {
  const [tokenInfo, setTokenInfo] = useState<TokenInfo | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdate, setLastUpdate] = useState<Date | null>(null);

  const fetchPrice = useCallback(async () => {
    if (!tokenAddress) return;
    
    setLoading(true);
    setError(null);
    try {
      const info = await JupiterApiService.getTokenPrice(tokenAddress);
      setTokenInfo(info);
      setLastUpdate(new Date());
    } catch (err) {
      setError('Erreur lors de la récupération du prix');
      console.error('Erreur useTokenPrice:', err);
    } finally {
      setLoading(false);
    }
  }, [tokenAddress]);

  useEffect(() => {
    fetchPrice();
    const interval = setInterval(fetchPrice, refreshInterval);
    return () => clearInterval(interval);
  }, [fetchPrice, refreshInterval]);

  return { 
    tokenInfo, 
    loading, 
    error, 
    lastUpdate,
    refetch: fetchPrice 
  };
};
